// MonitorDetail — T059 + T065
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import StatusBadge from '../components/StatusBadge';
import MetricsChart from '../components/MetricsChart';
import UptimeChart from '../components/UptimeChart';
import { getMonitor, getMetrics, getIncidents, updateMonitor, deleteMonitor, checkMonitorNow, type Monitor, type Metric, type Incident } from '../services/api';

export default function MonitorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [monitor, setMonitor] = useState<Monitor | null>(null);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editingRecipients, setEditingRecipients] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkMessage, setCheckMessage] = useState<{ text: string; ok: boolean } | null>(null);

  // Load metrics independently (FR-013/FR-014 support)
  const loadMetrics = async () => {
    if (!id) return;
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const met = await getMetrics(id, { from: sevenDaysAgo, limit: '2000' });
      setMetrics(met.data || []);
    } catch (err: any) {
      setMetricsError(err.message || 'Failed to load metrics');
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    // Load monitor and incidents together; metrics load independently
    Promise.all([
      getMonitor(id),
      getIncidents(id),
    ]).then(([mon, inc]) => {
      setMonitor(mon);
      setIncidents(inc.data || []);
    }).catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    loadMetrics();
  }, [id]);

  const handleDelete = async () => {
    if (!id || !confirm('Delete this monitor?')) return;
    try {
      await deleteMonitor(id);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleVerifyDns = async () => {
    if (!id || !monitor) return;
    try {
      const updated = await updateMonitor(id, { dnsMutated: false } as any);
      setMonitor(updated);
    } catch (err: any) {
      setError(err.message || 'Failed to verify DNS');
    }
  };

  const handleCheckNow = async () => {
    if (!id || checking) return;
    setChecking(true);
    setCheckMessage(null);
    try {
      await checkMonitorNow(id);
      setCheckMessage({ text: 'Check enqueued — result in a few seconds', ok: true });
      // Poll for the updated monitor state shortly after the worker processes the queue message
      setTimeout(async () => {
        try {
          const [mon, inc] = await Promise.all([getMonitor(id), getIncidents(id)]);
          setMonitor(mon);
          setIncidents(inc.data || []);
          setCheckMessage({ text: `Result: ${mon.state}`, ok: mon.state === 'UP' });
        } catch {
          setCheckMessage({ text: 'Check enqueued — refresh to see result', ok: true });
        }
        setChecking(false);
      }, 5000);
    } catch (err: any) {
      setCheckMessage({ text: err.message || 'Failed to enqueue check', ok: false });
      setChecking(false);
    }
  };

  const addRecipient = async (r: { type: 'email' | 'sms'; address: string }) => {
    if (!monitor || !id) return;
    const newRecipients = [...monitor.recipients, r];
    try {
      const updated = await updateMonitor(id, { recipients: newRecipients } as any);
      setMonitor(updated);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const removeRecipient = async (index: number) => {
    if (!monitor || !id) return;
    const newRecipients = monitor.recipients.filter((_, i) => i !== index);
    try {
      const updated = await updateMonitor(id, { recipients: newRecipients } as any);
      setMonitor(updated);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div className="animate-pulse bg-surface rounded-lg h-64" />;
  }

  if (error || !monitor) {
    return <div className="bg-status-down/20 text-status-down rounded-lg px-4 py-3">{error || 'Monitor not found'}</div>;
  }

  // Compute uptime from metrics (primary) or API field (fallback)
  const computedUptime = metrics.length > 0
    ? (metrics.filter((m) => m.state === 'UP').length / metrics.length) * 100
    : monitor.uptimePercent ?? null;

  // IP Residence Duration
  const ipResidence = monitor.lastDnsMutationAt
    ? formatDuration(Date.now() - new Date(monitor.lastDnsMutationAt).getTime())
    : (monitor.knownIPs && monitor.knownIPs.length > 0) ? 'Since first check' : 'N/A';

  return (
    <div className="space-y-6">
      {/* DNS Mutation Warning Banner */}
      {monitor.dnsMutated && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h4 className="text-amber-500 font-semibold">DNS Mutation Detected</h4>
              <p className="text-text-secondary text-sm">
                The DNS records for this host have mutated since your baseline. IP addresses have changed.
              </p>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={handleVerifyDns}
              className="bg-amber-600 hover:bg-amber-700 text-white font-medium rounded px-4 py-2 text-sm min-h-[44px] transition-colors"
            >
              Verify DNS & Clear Flag
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => navigate('/')} className="text-text-secondary hover:text-text-primary text-sm mb-2 inline-block">&larr; Back</button>
          <h1 className="text-white text-h1">{monitor.name}</h1>
          <a href={monitor.url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-hover underline text-sm mt-1 inline-block">{monitor.url}</a>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge state={monitor.state} size="lg" />
          {isAdmin && (
            <div className="flex gap-2 items-center">
              {checkMessage && (
                <span className={`text-xs ${checkMessage.ok ? 'text-status-up' : 'text-status-down'}`}>
                  {checkMessage.text}
                </span>
              )}
              <button
                onClick={handleCheckNow}
                disabled={checking}
                className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white rounded px-3 py-2 text-sm min-h-[44px]"
                title="Run an on-demand check now"
              >
                {checking ? 'Checking…' : 'Test'}
              </button>
              <button onClick={() => setEditing(!editing)} className="bg-surface hover:bg-surface-hover text-text-primary rounded px-3 py-2 text-sm min-h-[44px] border border-border">
                Edit
              </button>
              <button onClick={handleDelete} className="bg-status-down/20 hover:bg-status-down/30 text-status-down rounded px-3 py-2 text-sm min-h-[44px]">
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit form */}
      {editing && isAdmin && <EditForm monitor={monitor} onSaved={(m) => { setMonitor(m); setEditing(false); }} />}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Uptime" value={computedUptime != null ? `${computedUptime.toFixed(1)}%` : 'N/A'} />
        <StatCard label="Known IPs" value={monitor.knownIPs && monitor.knownIPs.length > 0 ? monitor.knownIPs.join(', ') : 'N/A'} />
        <StatCard label="IP Residence" value={ipResidence} />
        <StatCard label="Failures" value={String(monitor.consecutiveFailures)} />
      </div>

      {/* CNAME */}
      {monitor.cname && (
        <div className="bg-surface rounded-lg px-4 py-2 border border-border">
          <span className="text-text-muted text-xs uppercase tracking-wider mr-2">CNAME</span>
          <span className="text-text-primary text-sm">{monitor.cname}</span>
        </div>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        {monitor.hostingProvider && <span className="bg-surface text-text-secondary text-sm px-3 py-1 rounded-full border border-border">Hosting Provider: {monitor.hostingProvider}</span>}
        {monitor.providerAccount && <span className="bg-surface text-text-secondary text-sm px-3 py-1 rounded-full border border-border">Provider Account: {monitor.providerAccount}</span>}
        {monitor.client && <span className="bg-surface text-text-secondary text-sm px-3 py-1 rounded-full border border-border">Client: {monitor.client}</span>}
      </div>

      {/* Recipients */}
      <div className="bg-surface rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-text-primary font-semibold">Alert Recipients</h3>
          {isAdmin && !editingRecipients && (
            <button onClick={() => setEditingRecipients(true)} className="text-brand text-sm hover:underline">Manage</button>
          )}
        </div>
        {monitor.recipients.length === 0 && !editingRecipients && (
          <p className="text-text-muted text-sm">No recipients configured</p>
        )}
        <div className="space-y-1">
          {monitor.recipients.map((r, i) => (
            <div key={i} className="text-text-secondary text-sm flex items-center gap-2">
              <span className="text-text-muted">{r.type === 'email' ? '📧' : '📱'}</span>
              <span className="flex-1">{r.address}</span>
              {editingRecipients && (
                <button onClick={() => removeRecipient(i)} className="text-status-down text-xs hover:underline">remove</button>
              )}
            </div>
          ))}
        </div>
        {editingRecipients && isAdmin && (
          <RecipientsEditor
            onAdd={(r) => addRecipient(r)}
            onDone={() => setEditingRecipients(false)}
          />
        )}
      </div>

      {/* Uptime Chart — FR-013 loading / FR-014 error+retry */}
      <UptimeChart metrics={metrics} loading={metricsLoading} error={metricsError} onRetry={loadMetrics} />

      {/* Response Time Chart */}
      <MetricsChart metrics={metrics} />

      {/* Incidents */}
      <div className="bg-surface rounded-lg p-4 border border-border">
        <h3 className="text-text-primary font-semibold mb-3">Incidents</h3>
        {incidents.length === 0 ? (
          <p className="text-text-muted text-sm">No incidents recorded</p>
        ) : (
          <div className="space-y-2">
            {incidents.map((inc) => (
              <div key={inc.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                <div className="flex items-center gap-2">
                  <StatusBadge state={inc.state === 'OPEN' ? 'DOWN' : 'UP'} size="sm" />
                  <span className="text-text-secondary">{new Date(inc.openedAt).toLocaleString()}</span>
                </div>
                <span className="text-text-muted">
                  {(inc.closedAt || inc.resolvedAt) ? `Resolved ${new Date((inc.closedAt || inc.resolvedAt)!).toLocaleString()}` : 'Ongoing'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface rounded-lg p-4 border border-border">
      <div className="text-text-muted text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-text-primary text-lg font-semibold truncate">{value}</div>
    </div>
  );
}

function EditForm({ monitor, onSaved }: { monitor: Monitor; onSaved: (m: Monitor) => void }) {
  const [name, setName] = useState(monitor.name);
  const [url, setUrl] = useState(monitor.url);
  const [interval, setInterval] = useState(monitor.interval);
  const [maxRetries, setMaxRetries] = useState(monitor.maxRetries);
  const [hostingProvider, setHostingProvider] = useState(monitor.hostingProvider || '');
  const [providerAccount, setProviderAccount] = useState(monitor.providerAccount || '');
  const [client, setClient] = useState(monitor.client || '');
  const [muted, setMuted] = useState(monitor.muted);
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>(
    monitor.headers
      ? Object.entries(monitor.headers)
          .filter(([key]) => key.toLowerCase() !== 'user-agent')
          .map(([key, value]) => ({ key, value }))
      : []
  );
  // User-Agent is exposed as a dedicated field; stored back into headers['User-Agent'].
  const existingUA = monitor.headers
    ? Object.entries(monitor.headers).find(([key]) => key.toLowerCase() === 'user-agent')?.[1]
    : undefined;
  const [userAgent, setUserAgent] = useState(existingUA || '');
  const [knownIPs, setKnownIPs] = useState<string[]>(monitor.knownIPs || []);
  const [newIP, setNewIP] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const addHeader = () => setHeaders((prev) => [...prev, { key: '', value: '' }]);
  const updateHeader = (i: number, field: 'key' | 'value', val: string) =>
    setHeaders((prev) => prev.map((h, idx) => idx === i ? { ...h, [field]: val } : h));
  const removeHeader = (i: number) => setHeaders((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const headersObj: Record<string, string> = {};
      headers.forEach((h) => { if (h.key.trim()) headersObj[h.key.trim()] = h.value; });
      // Dedicated User-Agent field wins; empty means "use the default CM-Monitor/1.0"
      if (userAgent.trim()) headersObj['User-Agent'] = userAgent.trim();

      const updated = await updateMonitor(monitor.id, {
        name, url, interval, maxRetries, muted,
        hostingProvider: hostingProvider || undefined,
        providerAccount: providerAccount || undefined,
        client: client || undefined,
        headers: Object.keys(headersObj).length > 0 ? headersObj : undefined,
        knownIPs,
      } as any);
      onSaved(updated);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface rounded-lg p-4 border border-border space-y-3">
      {error && <div className="text-status-down text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-text-muted text-xs">Name</label>
          <input type="text" aria-label="Monitor name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" required />
        </div>
        <div>
          <label className="text-text-muted text-xs">URL</label>
          <input type="url" aria-label="Monitor URL" value={url} onChange={(e) => setUrl(e.target.value)}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" required />
        </div>
        <div>
          <label className="text-text-muted text-xs">Poll Interval (seconds)</label>
          <input type="number" aria-label="Poll interval" value={interval} onChange={(e) => setInterval(Number(e.target.value))} min={30}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-text-muted text-xs">Max Retries</label>
          <input type="number" aria-label="Max retries" value={maxRetries} onChange={(e) => setMaxRetries(Number(e.target.value))} min={0} max={10}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Tags */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-text-muted text-xs">Hosting Provider</label>
          <input type="text" value={hostingProvider} onChange={(e) => setHostingProvider(e.target.value)}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" placeholder="AWS, Azure..." />
        </div>
        <div>
          <label className="text-text-muted text-xs">Provider Account</label>
          <input type="text" value={providerAccount} onChange={(e) => setProviderAccount(e.target.value)}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-text-muted text-xs">Client</label>
          <input type="text" value={client} onChange={(e) => setClient(e.target.value)}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Muted */}
      <label className="flex items-center gap-2 text-text-secondary text-sm cursor-pointer">
        <input type="checkbox" checked={muted} onChange={(e) => setMuted(e.target.checked)} className="w-4 h-4 accent-brand" />
        Muted (suppress alerts)
      </label>

      {/* Known IPs (FR-036) */}
      <div>
        <label className="text-text-muted text-xs block mb-1">Known IPs</label>
        <div className="flex flex-wrap gap-2 mb-1">
          {knownIPs.map((ip, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm">
              {ip}
              <button type="button" onClick={() => setKnownIPs(prev => prev.filter((_, idx) => idx !== i))} className="text-status-down text-xs hover:underline ml-1">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" placeholder="e.g. 192.168.1.1" value={newIP} onChange={(e) => setNewIP(e.target.value)}
            className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (newIP.trim() && !knownIPs.includes(newIP.trim())) {
                  setKnownIPs(prev => [...prev, newIP.trim()]);
                  setNewIP('');
                }
              }
            }} />
          <button type="button" onClick={() => {
            if (newIP.trim() && !knownIPs.includes(newIP.trim())) {
              setKnownIPs(prev => [...prev, newIP.trim()]);
              setNewIP('');
            }
          }} className="text-brand text-sm hover:underline">+ Add</button>
        </div>
      </div>

      {/* User-Agent (dedicated — overrides the CM-Monitor/1.0 default) */}
      <div>
        <label className="text-text-muted text-xs block mb-1">User-Agent</label>
        <input type="text" placeholder="CM-Monitor/1.0 (default)" value={userAgent} onChange={(e) => setUserAgent(e.target.value)}
          className="w-full bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm" />
        <p className="text-text-muted text-xs mt-1">Leave empty to send the default <code>CM-Monitor/1.0</code>. A value here overrides it — useful for bypass rules keyed on User-Agent.</p>
      </div>

      {/* HTTP Headers */}
      <div>
        <label className="text-text-muted text-xs block mb-1">Custom HTTP Headers</label>
        {headers.map((h, i) => (
          <div key={i} className="flex gap-2 mb-1">
            <input type="text" placeholder="Header-Name" value={h.key} onChange={(e) => updateHeader(i, 'key', e.target.value)}
              className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm" />
            <input type="text" placeholder="value" value={h.value} onChange={(e) => updateHeader(i, 'value', e.target.value)}
              className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm" />
            <button type="button" onClick={() => removeHeader(i)} className="text-status-down text-xs hover:underline">×</button>
          </div>
        ))}
        <button type="button" onClick={addHeader} className="text-brand text-sm hover:underline">+ Add Header</button>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={loading} className="bg-brand text-white rounded px-4 py-2 text-sm min-h-[44px] disabled:opacity-50">
          {loading ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={() => onSaved(monitor)} className="bg-surface-hover text-text-secondary rounded px-4 py-2 text-sm min-h-[44px]">Cancel</button>
      </div>
    </form>
  );
}

function RecipientsEditor({ onAdd, onDone }: { onAdd: (r: { type: 'email' | 'sms'; address: string }) => void; onDone: () => void }) {
  const [type, setType] = useState<'email' | 'sms'>('email');
  const [address, setAddress] = useState('');

  const handleAdd = () => {
    if (!address.trim()) return;
    onAdd({ type, address: address.trim() });
    setAddress('');
  };

  return (
    <div className="mt-2 pt-2 border-t border-border">
      <div className="flex gap-2 items-center">
        <select value={type} onChange={(e) => setType(e.target.value as 'email' | 'sms')}
          className="bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm">
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
        <input type="text" placeholder={type === 'email' ? 'user@example.com' : '+1234567890'}
          value={address} onChange={(e) => setAddress(e.target.value)}
          className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm"
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())} />
        <button type="button" onClick={handleAdd} className="text-brand text-sm hover:underline">+ Add</button>
        <button type="button" onClick={onDone} className="text-text-muted text-sm hover:underline">Done</button>
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  const minutes = Math.floor(ms / 60000);
  return `${minutes}m`;
}
